import { Avatar, Button, Dropdown, Flex, type MenuProps } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AiOutlineDownload,
  AiOutlineFileText,
  AiOutlineMenuFold,
  AiOutlineMenuUnfold,
  AiOutlineQuestionCircle,
  AiOutlineUser,
} from "react-icons/ai";
import { MdLogout } from "react-icons/md";
import { useNavigate } from "react-router";

import { STORAGE_KEYS } from "sandbox/constants";
import { useSandboxAuth } from "sandbox/contexts/SandboxAuthContext";
import { ThemeToggle } from "sandbox/features/shared/ThemeToggle";
import { useSandboxLogo } from "sandbox/hooks";
import { sandboxPaths } from "sandbox/paths";
import { useLocalStorage } from "shell/api/hooks";

import { DownloadCollectionModal } from "./DownloadCollectionModal";
import s from "./SandboxHeader.module.css";
import { SandboxHelpModal } from "./SandboxHelpModal";
import { UpdatePasswordModal } from "./UpdatePasswordModal";

interface SandboxHeaderProps {
  collapsed?: boolean;
  onToggleCollapse: () => void;
}

export const SandboxHeader = ({
  collapsed = false,
  onToggleCollapse,
}: SandboxHeaderProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { user, signOut } = useSandboxAuth();
  const navigate = useNavigate();
  const logo = useSandboxLogo();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [tourDismissed, setTourDismissed] = useLocalStorage(
    STORAGE_KEYS.tourDismissed,
    false,
  );
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  useEffect(() => {
    if (user && !tourDismissed) {
      setHelpModalOpen(true);
    }
  }, [user]);

  const closeHelpModal = () => {
    setHelpModalOpen(false);
    setTourDismissed(true);
  };

  const handleSignOut = () => {
    signOut();
    navigate(sandboxPaths.login, { replace: true });
  };

  const items: MenuProps["items"] = [
    {
      key: "userInfo",
      label: (
        <div>
          <div className={s.userName}>
            {user?.firstName} {user?.lastName}
          </div>
          <div className={s.userEmail}>{user?.email}</div>
        </div>
      ),
      disabled: true,
    },
    {
      key: "change-password",
      label: t("components.sandboxHeader.changePassword"),
      onClick: () => setPasswordModalOpen(true),
    },
    { type: "divider" },
    {
      key: "sign-out",
      icon: <MdLogout />,
      label: t("components.sandboxHeader.logout"),
      onClick: handleSignOut,
    },
  ];

  return (
    <header className={s.header}>
      <section className={s.left}>
        <Button
          type="text"
          onClick={onToggleCollapse}
          aria-label={
            collapsed
              ? t("components.sandboxHeader.expandMenu")
              : t("components.sandboxHeader.collapseMenu")
          }
        >
          {collapsed ? <AiOutlineMenuUnfold /> : <AiOutlineMenuFold />}
        </Button>
        <img src={logo} alt="Thredd" className={s.logo} />
        <span className={s.brandPill}>{t("components.header.brandPill")}</span>
      </section>
      <section className={s.right}>
        <Flex vertical justify="center">
          <Button type="text" onClick={() => setDownloadModalOpen(true)}>
            <AiOutlineDownload className={s.icon} />{" "}
            <span className={s.buttonLabel}>
              {t("components.sandboxHeader.postmanCollection")}
            </span>
          </Button>
        </Flex>
        <Flex vertical justify="center">
          <Button
            type="text"
            onClick={() => {
              window.open(
                "https://cardsapidocs.thredd.com/v2.0/docs/new-sandbox-v2",
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <AiOutlineFileText className={s.icon} />{" "}
            <span className={s.buttonLabel}>
              {t("common:components.userMenu.documentationPortal")}
            </span>
          </Button>
        </Flex>
        <Flex vertical justify="center">
          <ThemeToggle className={s.themeToggle} />
        </Flex>
        <Flex vertical justify="center">
          <Button
            type="text"
            onClick={() => setHelpModalOpen(true)}
            aria-label={t("sandbox:components.sandboxHeader.sandboxHelp")}
          >
            <AiOutlineQuestionCircle className={s.iconLg} />
          </Button>
        </Flex>
        {user && (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              className={s.profileButton}
              data-testid="sandbox-header-profile-button"
            >
              <Avatar shape="square" size={28} className={s.avatar}>
                <AiOutlineUser />
              </Avatar>
            </Button>
          </Dropdown>
        )}
      </section>
      <UpdatePasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
      {helpModalOpen && (
        <SandboxHelpModal open={helpModalOpen} onClose={closeHelpModal} />
      )}
      {downloadModalOpen && (
        <DownloadCollectionModal
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
        />
      )}
    </header>
  );
};
